'use client';

import { useEffect } from 'react';

/**
 * Restores keyboard scrolling inside MUI dialogs.
 *
 * In a MUI `<Dialog>` (default `scroll="paper"`) the scrollable region is the
 * `.MuiDialogContent-root` element, but the dialog's focus trap parks focus on
 * an *ancestor* — the `.MuiDialog-container` (which MUI gives `outline: 0` for
 * exactly this reason). The browser only scrolls the focused element's
 * scrollable ancestors — never its descendants — so Arrow / Page / Home / End
 * keys do nothing: the same content scrolls fine on a normal page but not once
 * it's inside a dialog.
 *
 * This installs a single document-level listener that forwards those keys to
 * the dialog's content area, but *only* when focus sits outside that area
 * (e.g. on the dialog container itself or on an action button — which is where
 * the focus trap leaves it when the dialog opens). When focus is already inside the
 * content — an input, a list, the scroll region itself — the browser or that
 * control handles the key natively and we stay out of the way, so we never
 * double-scroll or hijack the text cursor / option navigation.
 *
 * `Space`/`Shift+Space` are intentionally left out: a dialog's default focus is
 * often a button or switch, and those keys must keep activating it.
 *
 * Call once, high in the tree (e.g. from the app's ThemeRegistry), so every
 * dialog in the app benefits without per-dialog wiring.
 */
export function useDialogKeyboardScroll(): void {
  useEffect(() => {
    // Approx. one browser "line" per Arrow press (Chrome's default is ~40px).
    const LINE_STEP = 40;

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.defaultPrevented) return;
      // Leave browser/OS shortcuts (e.g. Cmd+ArrowDown) untouched.
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const key = event.key;
      if (
        key !== 'ArrowUp' &&
        key !== 'ArrowDown' &&
        key !== 'PageUp' &&
        key !== 'PageDown' &&
        key !== 'Home' &&
        key !== 'End'
      ) {
        return;
      }

      const target = event.target as HTMLElement | null;
      // The keystroke must originate inside an open dialog. `.MuiDialog-container`
      // is the element the focus trap focuses on open, and it wraps the paper,
      // content and actions — so this matches wherever focus currently sits.
      const dialog = target?.closest('.MuiDialog-container');
      if (!dialog) return;

      const content = dialog.querySelector<HTMLElement>('.MuiDialogContent-root');
      if (!content) return;

      // Focus already sits inside the scroll region (the content, a field, a
      // list, …). The browser or that control handles the key natively — bail
      // so we don't double-scroll or steal cursor / option-navigation keys.
      if (content.contains(target)) return;

      // Defensive: an editable / arrow-consuming control living outside the
      // content area (rare — e.g. a search field rendered in the title bar).
      if (
        target?.closest(
          'input, textarea, select, [contenteditable], [role="textbox"], [role="combobox"], [role="slider"], [role="spinbutton"]',
        )
      ) {
        return;
      }

      // Nothing to scroll — let the event pass through unchanged.
      if (content.scrollHeight <= content.clientHeight) return;

      const pageStep = content.clientHeight * 0.9;
      switch (key) {
        case 'ArrowUp':
          content.scrollBy({ top: -LINE_STEP });
          break;
        case 'ArrowDown':
          content.scrollBy({ top: LINE_STEP });
          break;
        case 'PageUp':
          content.scrollBy({ top: -pageStep });
          break;
        case 'PageDown':
          content.scrollBy({ top: pageStep });
          break;
        case 'Home':
          content.scrollTo({ top: 0 });
          break;
        case 'End':
          content.scrollTo({ top: content.scrollHeight });
          break;
      }
      event.preventDefault();
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);
}
